/** 按设置项 Schema 生成的表单行（放在 FormCard 里，字段解析见 schema-form.ts；与 iOS SchemaFormView 一致） */
import { Fragment } from "react";
import { ChoiceChip, NumberStepper, RowDivider, Switch, TextField, ValueRow } from "../ui";
import { fieldValue, type FormField, type FormValues, isFieldVisible } from "./schema-form";
import "./schema-form.css";

/** values 是当前值，没有的字段显示默认值；hints 给 page_range 这类控件用（如 page_count） */
export function SchemaFields({ fields, values, onChange, hints = {} }: { fields: FormField[]; values: FormValues; onChange: (v: FormValues) => void; hints?: Record<string, number> }) {
  const shown = fields.filter((f) => isFieldVisible(f, values, fields));
  const set = (k: string, v: FormValues[string]) => onChange({ ...values, [k]: v });
  return (
    <>
      {shown.map((f, i) => (
        <Fragment key={f.key}>
          {i > 0 && <RowDivider />}
          <FieldRow field={f} values={values} set={set} hints={hints} />
        </Fragment>
      ))}
    </>
  );
}

function Label({ f }: { f: FormField }) {
  return (
    <span className="kp-schema-label">
      <span>{f.title}</span>
      {f.description && <small>{f.description}</small>}
    </span>
  );
}

function FieldRow({ field: f, values, set, hints }: { field: FormField; values: FormValues; set: (k: string, v: FormValues[string]) => void; hints: Record<string, number> }) {
  const v = fieldValue(f, values);
  const k = f.kind;
  switch (k.kind) {
    case "integer": {
      const current = typeof v === "number" ? v : k.min;
      if (!k.options.length) {
        return (
          <ValueRow title={<Label f={f} />} height={56}>
            <NumberStepper value={current} min={k.min} max={Math.max(k.min, k.max)} onChange={(n) => set(f.key, n)} />
          </ValueRow>
        );
      }
      return (
        <div className="kp-schema-row">
          <Label f={f} />
          <div className="kp-schema-chips">
            {k.options.map((o) => (
              <ChoiceChip key={o} selected={current === o} onClick={() => set(f.key, o)}>
                {`${o}${k.unit ?? ""}`}
              </ChoiceChip>
            ))}
          </div>
        </div>
      );
    }
    case "bool":
      return <Switch label={<Label f={f} />} checked={v === true} onChange={(b) => set(f.key, b)} />;
    case "choice":
      return (
        <div className="kp-schema-row">
          <Label f={f} />
          <div className="kp-schema-chips">
            {k.values.map((o) => (
              <ChoiceChip key={o} selected={v === o} onClick={() => set(f.key, o)}>
                {k.labels[o] ?? o}
              </ChoiceChip>
            ))}
          </div>
        </div>
      );
    case "text":
      return (
        <div className="kp-schema-row">
          <TextField label={f.title} value={typeof v === "string" ? v : ""} onChange={(s) => set(f.key, s.slice(0, k.maxLength))} />
          {f.description && <small className="kp-schema-note">{f.description}</small>}
        </div>
      );
    case "page_range":
      return <PageRange f={f} value={v} set={set} count={hints.page_count ?? null} />;
  }
}

/** 页码范围：开关"全部 / 部分"，部分时两个步进器 */
function PageRange({ f, value, set, count }: { f: FormField; value: FormValues[string] | undefined; set: (k: string, v: FormValues[string]) => void; count: number | null }) {
  const r = Array.isArray(value) && value.length === 2 ? value.map((x) => (typeof x === "number" ? x : 1)) : null;
  const max = count ?? 999;
  const sub = r ? `第 ${r[0]} – ${r[1]} 页` : count != null ? `全部（共 ${count} 页）` : "全部";
  return (
    <div className="kp-schema-row">
      <Switch
        label={
          <span className="kp-schema-label">
            <span>{f.title}</span>
            <small>{sub}</small>
          </span>
        }
        checked={!!r}
        onChange={(on) => set(f.key, on ? [1, count ?? 1] : null)}
      />
      {r && (
        <>
          <ValueRow title="从第" height={52}>
            <NumberStepper value={r[0]!} min={1} max={max} onChange={(n) => set(f.key, [n, Math.max(n, r[1]!)])} />
          </ValueRow>
          <ValueRow title="到第" height={52}>
            <NumberStepper value={r[1]!} min={1} max={max} onChange={(n) => set(f.key, [Math.min(r[0]!, n), n])} />
          </ValueRow>
        </>
      )}
    </div>
  );
}
